import {
  Children,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, Menu } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Badge as ShadBadge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  平台 / 供应商后台的外壳
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这两个后台是桌面优先，其余全站是手机优先。**
 *    供应商要上传几十个 SKU、几百张图、维护参数表；平台运营要查订单、
 *    看组织树、核对个税台账 —— 那些活在手机上做不了。
 *    买家端和分销商端（装修师傅在外面跑）是手机优先，取舍是反的。
 *
 * 🔴 **一屏十几个平级链接不叫导航，叫清单。**
 *    早先左栏是十八条同样大小的灰色小字，从「商品审核」一路排到「优惠券」——
 *    运营找一件事要从头读到尾，而且看不出哪些是每天要盯的、哪些是偶尔配一次的。
 *    现在按**做这件事的场景**分组（审核台 / 交易与资金 / 组织与客户 /
 *    运营 / 平台自营 / 系统），带图标，当前项左侧一条竖杠。
 *
 * 🔴 **后台的视觉语言跟商城不是一套。**
 *    商城是"逛"：大图、留白、圆角、少而大的元素。
 *    后台是"干活"：一屏要装下几十行数据，靠的是**密度、对齐和分区**——
 *    把商城那套 12px 大圆角白卡 + 居中大图标空态搬过来，就会变成
 *    "一整片白 + 几行小字"，看着像没做完。老板的原话是
 *    「完全是强行手机版改的，视觉效果太差」。
 *
 *    所以后台这边：内容区**浅灰底 + 白卡承载**（有边界感）、
 *    圆角收到 8px、正文 14px（不是手机那档 13）、
 *    表格行更紧、空态不用居中大图标。
 *
 * ⚠️ 颜色 token 仍然共用（`--primary`、`--border`、`--muted`…）——
 *    变的是尺寸和密度，不是配色。别在这里另写一套灰。
 */

export interface DeskNavItem {
  to: string;
  label: string;
  search?: Record<string, unknown>;
  icon?: LucideIcon;
  /** 右侧的数字（待办件数）。⚠️ 0 不显示 —— 一排灰色的 0 只是噪音。 */
  count?: number;
  /** 分组标题。同一个 group 的连续项会收在一个小标题下。 */
  group?: string;
}

export function Desk({
  title,
  subtitle,
  nav,
  children,
  toolbar,
  defaultTab,
  exits,
}: {
  title: string;
  /** 抬头下面那行小字：当前是谁、什么角色。 */
  subtitle?: string;
  nav: DeskNavItem[];
  children: ReactNode;
  toolbar?: ReactNode;
  /**
   * 侧栏底部的出口 —— **点了会离开后台**的链接放这儿，不许混进上面的导航。
   *
   * 🔴 早先把「进货」「定价」「优惠券」直接塞进了后台导航，
   *    而那三条指的是**前台的商家页** —— 运营在后台点一下优惠券，
   *    整个界面换成了商城的账户中心，`/restock` 还会顺手把身份切成"进货"
   *    然后跳到商品列表。老板的原话：「我不希望看见这种混乱的效果」。
   *
   * ⚠️ 判断标准很简单：**这条路由是不是 `/admin` 或 `/supplier` 开头**。
   *    不是的话它就不属于导航，只能出现在这个出口区，而且要标明"另一套界面"。
   */
  exits?: { to: string; label: string; hint?: string }[];
  /**
   * URL 上没带 `?tab=` 时算哪一项是当前项。
   * ⚠️ 不传的话首页那一项永远不高亮，抬头也只能显示后台名 ——
   *    因为 `search.tab` 是 undefined，跟任何一项都对不上。
   */
  defaultTab?: string;
}) {
  /**
   * 手机上的抽屉开着没有。
   * ⚠️ **切页要自动关**（下面的 effect）—— 不关的话点完一项，
   *    抽屉还盖在内容上，用户得再点一次遮罩才看得见自己点出来的东西。
   */
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search }) as { tab?: string };

  /**
   * ⚠️ **切页要自动收起抽屉。** 不收的话点完一项，抽屉还盖在内容上 ——
   *    用户得再点一次遮罩才看得见自己刚点出来的东西。
   *    ⚠️ 依赖里 `search.tab` 不能少：后台大部分"换页"只改 `?tab=`，
   *    pathname 一直是 `/admin`，只看 pathname 的话点同一个后台里的
   *    任何一项抽屉都不会关。
   */
  useEffect(() => {
    setNavOpen(false);
  }, [path, search.tab]);

  /**
   * 🔴 **给手机上的卡片自动补列名 —— 包括那 11 张裸 `<table>`。**
   *
   *    共用的 `Table`/`Td` 会自己带 `data-label`，但后台里还有十来处是
   *    直接写的 `<table><td>`（历史写法）。手机上它们同样被 CSS 改成了卡片，
   *    但**没有列名** —— 而没有列名的一列数字（`¥1800.00` 到底是供货价
   *    还是平台价）比表格更难读。
   *
   *    逐个改那 11 处要动很多行、而且以后新写的还会漏。这里统一在运行时
   *    按列序号从 `thead th` 上取文字补上，一劳永逸。
   *
   * ⚠️ **只补没有的**（`hasAttribute` 判一下）：共用组件已经标好了，
   *    重复写一遍只是浪费。
   * ⚠️ 用 `MutationObserver` 而不是只跑一次 —— 后台的表是异步取数渲染的，
   *    effect 跑的时候多半还是空的。
   * ⚠️ 只在窄屏跑：桌面上表头就在那儿，补这个属性没有意义。
   */
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !window.matchMedia("(max-width: 767px)").matches) return;

    const stamp = () => {
      for (const table of root.querySelectorAll("table")) {
        const heads = [...table.querySelectorAll("thead th")].map((th) =>
          (th.textContent ?? "").trim(),
        );
        if (heads.length === 0) continue;
        for (const row of table.querySelectorAll("tbody tr")) {
          [...row.children].forEach((cell, i) => {
            if (cell.tagName === "TD" && !cell.hasAttribute("data-label")) {
              cell.setAttribute("data-label", heads[i] ?? "");
            }
          });
        }
      }
    };

    stamp();
    const mo = new MutationObserver(stamp);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [path, search.tab]);

  const currentTab = search.tab ?? defaultTab;
  const isActive = (n: DeskNavItem) => {
    const tab = (n.search as { tab?: string } | undefined)?.tab;
    /** ⚠️ 后台用 `?tab=` 分页签，光比 pathname 会让所有项同时高亮。 */
    return tab ? path === n.to && currentTab === tab : path === n.to;
  };

  /** 当前这一项 —— 内容区的标题直接用它，省得每个页面自己写一遍。 */
  const current = nav.find(isActive);

  /** 把连续同组的项收在一起（顺序按传进来的顺序，不重排）。 */
  const groups: { name?: string; items: DeskNavItem[] }[] = [];
  for (const n of nav) {
    const last = groups[groups.length - 1];
    if (last && last.name === n.group) last.items.push(n);
    else groups.push({ name: n.group, items: [n] });
  }

  return (
    /*
      🔴 **整个后台锁在一屏里，滚动交给里面两块各自处理。**
         原来是 `min-h-dvh`：左栏那二十来项导航把**整页**撑到一千四百多高，
         于是 `nav` 上写着的 `overflow-y-auto`（"导航自己滚动，不跟着内容走"）
         **根本没生效** —— 它的父级没有确定的高度，`flex-1` 就约束不了它。

         后果是老板圈的那个：内容区明明只有七百多高，页面却有一千四，
         内容下面凭空多出小半屏白 —— 看着像页面没加载完。

      ⚠️ 换成 `h-dvh + overflow-hidden` 之后，**内容区必须自己能滚**
         （下面那个 `overflow-y-auto`），否则长表格会被裁掉看不见。
    */
    <div className="flex h-dvh overflow-hidden bg-muted/40">
      {/*
        ══════════ 手机上的遮罩 ══════════
        ⚠️ 抽屉开着时点内容区要能关掉它 —— 只给一个关闭按钮的话，
           用户的第一反应是点旁边，点了没反应就以为卡住了。
      */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}

      {/*
        ══════════ 左：导航 ══════════
        🔴 **手机上是抽屉，不是缩窄的侧栏。**
           248px 的固定侧栏在 390 宽的屏幕上要吃掉三分之二，剩下的地方
           连一张表都放不下 —— 那不是适配，是把桌面版塞进手机。
           所以 `< lg` 时它整个移出屏幕，靠抬头上的汉堡拉出来。
        ⚠️ 用 `translate-x` 不用 `display:none`：有过渡动画，
           而且抽屉里的滚动位置不会每次重置。
      */}
      <aside
        className={cn(
          "flex w-[248px] shrink-0 flex-col border-r border-border bg-background",
          "max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-40 max-lg:w-[280px]",
          "max-lg:transition-transform max-lg:duration-200",
          navOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full",
        )}
      >
        {/* ⚠️ 抬头给足高度并跟内容区的 header 对齐（都是 64）—— 差几像素会看出错位。 */}
        <div className="flex h-16 shrink-0 flex-col justify-center border-b border-border px-5">
          <p className="text-[15px] font-medium">{title}</p>
          {subtitle && <p className="mt-0.5 text-[12px] text-muted-foreground">{subtitle}</p>}
        </div>

        {/* ⚠️ 导航自己滚动，不跟着内容走 —— 十几条加上分组标题会超过一屏。 */}
        <nav className="min-h-0 flex-1 overflow-y-auto p-2">
          {groups.map((g, gi) => (
            <div key={g.name ?? gi} className={gi > 0 ? "mt-3" : undefined}>
              {/* ⚠️ 分组标题原来是 11px/70% 灰，几乎看不见 —— 等于白分了组。 */}
              {g.name && (
                <p className="px-3 pt-1.5 pb-1.5 text-[12px] font-medium tracking-wider text-muted-foreground">
                  {g.name}
                </p>
              )}
              <div className="space-y-0.5">
                {g.items.map((n) => {
                  const active = isActive(n);
                  const Icon = n.icon;
                  return (
                    <Link
                      key={n.to + JSON.stringify(n.search ?? {})}
                      to={n.to}
                      search={(n.search ?? {}) as never}
                      className={cn(
                        "relative flex items-center gap-2.5 rounded-md py-[7px] pr-2.5 pl-3 text-[14px] transition-colors",
                        active
                          ? "bg-brand-50 font-medium text-brand-700"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                      )}
                    >
                      {/* ⚠️ 一条竖杠比整块底色更好认 —— 底色在长列表里会糊成一片。 */}
                      {active && (
                        <span className="absolute top-1.5 bottom-1.5 left-0 w-[3px] rounded-full bg-brand-600" />
                      )}
                      {Icon && <Icon className="size-4 shrink-0" />}
                      <span className="min-w-0 flex-1 truncate">{n.label}</span>
                      {/* ⚠️ 0 不显示：一排灰色的 0 只是噪音，有数字才代表"有事要做"。 */}
                      {!!n.count && n.count > 0 && (
                        <span className="shrink-0 rounded-full bg-destructive px-1.5 text-[11px] leading-[17px] font-medium text-white tabular-nums">
                          {n.count > 99 ? "99+" : n.count}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/*
          ⚠️ 出口区跟上面的导航**明确分开**：加一条分隔、一个小标题、
             每条右边一个"跳出去"的箭头 —— 让人在点之前就知道这一下会离开后台。
        */}
        <div className="border-t border-border p-2">
          <p className="px-3 pt-1 pb-1.5 text-[12px] font-medium tracking-wider text-muted-foreground">
            离开后台
          </p>
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <ArrowLeft className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">回商城</span>
          </Link>
          {(exits ?? []).map((e) => (
            <Link
              key={e.to}
              to={e.to}
              search={{} as never}
              className="flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <ExternalLink className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">
                {e.label}
                {e.hint && (
                  <span className="mt-0.5 block text-[11px] text-muted-foreground/70">
                    {e.hint}
                  </span>
                )}
              </span>
            </Link>
          ))}
        </div>
      </aside>

      {/* ══════════ 右：内容 ══════════ */}
      <main className="flex min-w-0 flex-1 flex-col">
        {/*
          🔴 **内容区要有自己的抬头。**
             早先点进任何一页，右边直接就是一张表 —— 除了左栏那一条高亮，
             没有任何东西告诉你"这是哪一页、这一页是干什么的"。
             标题从当前导航项来，每页不用自己写一遍。
        */}
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background px-4 lg:gap-4 lg:px-7">
          {/*
            ⚠️ 汉堡只在手机上出现（`lg:hidden`）。点击区 40px ——
               后台在手机上多半是单手在用，小于 40 很容易点空。
          */}
          <button
            className="-ml-1 flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground lg:hidden"
            aria-label="打开菜单"
            onClick={() => setNavOpen(true)}
          >
            <Menu className="size-5" />
          </button>
          <h1 className="min-w-0 truncate text-[16px] font-medium lg:text-[18px]">
            {current?.label ?? title}
          </h1>
          {toolbar && (
            <div className="ml-auto flex shrink-0 items-center gap-2 lg:gap-3">{toolbar}</div>
          )}
        </header>

        {/*
          ⚠️ 内容区两侧 28、上下 24：24 在 1600 宽的屏幕上贴边贴得太紧。
          ⚠️ **自己滚**（外壳锁在一屏里了）。`min-h-0` 不能省 —— flex 子项默认
             最小高度是内容高度，不写的话它撑破容器，`overflow-y-auto` 白设。
        */}
        {/* ⚠️ 手机上两侧 12：给到 28 的话 390 宽的屏幕只剩 334 放内容。 */}
        <div
          ref={contentRef}
          className="desk-main min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-4 lg:px-7 lg:py-6"
        >
          {children}
        </div>
      </main>
    </div>
  );
}

/*
 * 🔴 **这里原来有个 `PageHead`（每页开头的说明），已经整个删掉了。别加回来。**
 *
 *    老板：「不要放这么多没有的页面说明」「删掉这些」。
 *
 *    它原本是一段解释"这一页是干什么的"的灰字。做过两版：
 *    第一版是顶在首屏上的三行大段灰字，正事被推到下面；
 *    第二版收成一行 +「展开说明」，看着还是每页顶着一条与内容无关的东西，
 *    而且每一页都多一个没人点的链接。
 *
 *    结论：**后台是天天用的工具，不是文档。** 一张表格 + 几个按钮，
 *    列名和按钮上的字就该说清楚它是什么；说不清楚的应该改列名和按钮文案，
 *    而不是在上面贴一段说明。
 *
 * ⚠️ 那些"按下去会发生什么"的话没有丢，它们该待在**动作旁边**：
 *    危险操作的确认弹窗（`ReasonDialog` 的 `description`）、
 *    表单字段下面的一行小字。那才是人真正会读到的位置。
 */

/** 后台里的一块内容卡。⚠️ 表格自带边框，别再往外套一层。 */
export function Panel({
  title,
  hint,
  action,
  children,
}: {
  title?: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-background">
      {(title || action) && (
        <div className="flex items-center gap-3 border-b border-border px-5 py-3">
          <div className="min-w-0">
            {title && <p className="text-[14px] font-medium">{title}</p>}
            {hint && <p className="mt-0.5 text-[12px] text-muted-foreground">{hint}</p>}
          </div>
          {action && <div className="ml-auto shrink-0">{action}</div>}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * 数据表。
 *
 * ⚠️ 宽表要能横向滚动而不是把页面撑破 —— 供应商的商品表列很多
 *    （名称、品类、规格数、库存、供货价、状态、时间），窄屏下必然放不下。
 * ⚠️ 表头**吸顶**：后台的表动辄几十行，滚下去之后不知道哪一列是哪一列。
 */
/**
 * 表头文字，给手机上的卡片用。
 *
 * 🔴 **手机上没有表头这一行**（卡片模式），所以每一格必须自己带列名 ——
 *    否则一张卡上就是一列没有标签的值，`¥1800.00` 到底是供货价还是平台价
 *    根本分不出来。
 * ⚠️ 用 context 传，不让每个页面重复写一遍 `data-label`：后台二十几页、
 *    上百个 `<Td>`，靠人工标一定会漏，而漏掉的那几格只在手机上才看得出来。
 */
const TableHeadContext = createContext<string[]>([]);
const ColIndexContext = createContext<number>(-1);

export function Table({
  head,
  children,
  /**
   * 撑满父容器的高度（父容器要自己有确定的高度）。
   *
   * ⚠️ 表格行数少的时候，下面会空一大片 —— 老板圈过。撑满之后表格区
   *    是一整块，行少就是块里空着，而不是"页面下半截没东西"。
   * ⚠️ 撑满时纵向也要能滚（`overflow-y-auto`），否则行一多就顶破容器。
   *    表头本来就是 `sticky top-0`，滚起来仍然跟着。
   */
  fill,
}: {
  head: string[];
  children: ReactNode;
  fill?: boolean;
}) {
  return (
    /*
      🔴 **手机上这不是"能横向滚的表格"，是一叠卡片。**
         后台的表动辄七八列、九百多宽。在 390 的屏幕上让它横向滚，
         意味着看一行要来回拉两三次，而且列名滚出去之后就不知道在看什么了 ——
         那是把桌面版塞进手机，不是适配。

         `desk-table` 这个类在 `styles.css` 里做的事：`< md` 时把
         table/tr/td 全改成 `display:block`，表头整行藏掉，每行变成一张卡，
         每格左边由 `::before` 打出自己的列名（`data-label`，见下面的 `Td`）。

      ⚠️ **用 CSS 做，不在 JS 里重排 DOM。** 各页传进来的 children 是
         `{items.map(...)}` 产生的数组，在这里拆开重组会破坏 key 和事件绑定，
         而且以后每加一页都要担心结构对不对。CSS 只挑显示方式，结构一份。
    */
    <TableHeadContext.Provider value={head}>
      <div
        className={cn(
          "desk-table overflow-x-auto rounded-lg border border-border bg-background",
          "max-md:rounded-none max-md:border-0 max-md:bg-transparent",
          fill && "min-h-0 flex-1 overflow-y-auto",
        )}
      >
        <table className="w-full min-w-[720px] text-[14px] max-md:min-w-0">
          <thead className="sticky top-0 z-10 bg-secondary/50 backdrop-blur">
            <tr className="border-b border-border text-left text-[12px] text-muted-foreground">
              {head.map((h) => (
                <th key={h} className="px-4 py-2.5 font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border max-md:divide-y-0">{children}</tbody>
        </table>
      </div>
    </TableHeadContext.Provider>
  );
}

export function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  const head = useContext(TableHeadContext);
  const col = useContext(ColIndexContext);
  return (
    <td
      /** 手机上由 CSS 的 `::before` 把它打在左边当列名。 */
      data-label={head[col] ?? ""}
      className={cn("px-4 py-3 align-middle", className)}
    >
      {children}
    </td>
  );
}

/**
 * 表格行里的操作。
 *
 * 🔴 **后台里这些不是"链接"，是按钮。**
 *    原来全站散落着 `className="text-xs text-brand-600"` 这种裸文字 ——
 *    四十多处、三种颜色、没有点击区、挤在行尾没有对齐感，
 *    看着像一段说明文字而不是"能按的东西"。
 *
 * ⚠️ 点击区给到 28px 高：后台一行只有 40 出头，纯文字链接很容易点空。
 */
export function RowAction({
  tone = "default",
  onClick,
  disabled,
  title,
  children,
}: {
  tone?: "default" | "primary" | "danger";
  onClick?: () => void;
  disabled?: boolean;
  /**
   * 滑上去的提示。
   * ⚠️ **按钮灰掉时一定要给一句** —— 一个点不动又不说话的按钮，
   *    运营只会以为页面坏了，然后来问客服。
   */
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        /**
         * ⚠️ **手机上点击区要加高**（`h-9` = 36，桌面仍是 28）。
         *    28px 在触屏上很容易点空，而这几个按钮里有「禁用」「删除」——
         *    点错的代价不是重来一次那么简单。
         *    ⚠️ 桌面不动：后台一行只有 40 出头，按钮跟着变高会把行撑散。
         */
        "inline-flex h-9 items-center rounded-md border px-3 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 md:h-7 md:px-2.5",
        tone === "primary" &&
          "border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50",
        tone === "danger" &&
          "border-destructive/25 text-destructive hover:border-destructive/40 hover:bg-destructive/5",
        tone === "default" && "border-border text-muted-foreground hover:bg-secondary hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

/** 一行里的几个操作。⚠️ 统一右对齐 + 间距，别每处各写一个 `space-x-3`。 */
export function RowActions({ children }: { children: ReactNode }) {
  /** ⚠️ 手机上按钮更高更宽，一行放不下时要能换行，否则会把卡片撑出屏幕。 */
  return <span className="flex flex-wrap justify-end gap-2 md:gap-1.5">{children}</span>;
}

/**
 * 表格里的一行。⚠️ 统一 hover，别每处各写一个 `hover:bg-neutral-50`。
 *
 * 🔴 **顺便把每一格的列序号发下去**，`Td` 靠它查自己的列名（手机上要显示）。
 *    只能在这儿数 —— `Td` 自己不知道排第几个。
 * ⚠️ 页面里直接写 `<tr>`／`<td>` 的（有几处历史写法）拿不到列名，
 *    手机上那几张卡会只有值没有标签。要么换成 `Tr`/`Td`，要么就这样。
 */
export function Tr({ children }: { children: ReactNode }) {
  return (
    <tr className="transition-colors hover:bg-secondary/40">
      {Children.map(children, (child, i) => (
        <ColIndexContext.Provider value={i}>{child}</ColIndexContext.Provider>
      ))}
    </tr>
  );
}

/**
 * 状态徽标。
 *
 * ⚠️ **颜色 + 文字双编码，不只靠颜色** —— 色弱用户也要能分辨。
 *    这里的 tone 名字是后台自己的语义（ok/warn/bad/mute），
 *    映到 shadcn 的 Badge 变体上。
 */
export function Badge({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "bad" | "mute";
  children: ReactNode;
}) {
  const variant = {
    ok: "success",
    warn: "warning",
    bad: "destructive",
    mute: "secondary",
  } as const;
  return <ShadBadge variant={variant[tone]}>{children}</ShadBadge>;
}

const PRODUCT_STATUS: Record<string, { label: string; tone: "ok" | "warn" | "bad" | "mute" }> = {
  draft: { label: "草稿", tone: "mute" },
  pending: { label: "待审核", tone: "warn" },
  active: { label: "已上架", tone: "ok" },
  rejected: { label: "已驳回", tone: "bad" },
  offline: { label: "已下架", tone: "mute" },
};

export function ProductStatusBadge({ status }: { status: string }) {
  const s = PRODUCT_STATUS[status] ?? { label: status, tone: "mute" as const };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}
